import React, { useCallback, useState } from 'react'
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useRouter } from 'expo-router'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { translate } from '../../i18n'
import { useConfirmDialog } from '../../components/ConfirmDialog'
import { LoungeAvatarPicker, LoungeTableColorPicker } from './components/LoungeCustomizationPickers'
import type { AvatarConfig } from './components/LoungeCustomizationPickers'
import { LoungeCodeCard } from './components/LoungeCodeCard'
import { useSaveLoungeJoinCode } from './components/useSaveLoungeJoinCode'

type CreatedLounge = {
  loungeId: Id<'lounges'>
  joinCode: string
  name: string
}

export function CreateLoungeScreen() {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin }
  } = useStore()

  const userId = user?.auth0Id ?? ''
  const mongoUserId = user?._id?.toString()
  const userName = user?.name ?? 'Unknown'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarConfig | null>(null)
  const [selectedColor, setSelectedColor] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdLounge, setCreatedLounge] = useState<CreatedLounge | null>(null)
  const [rotating, setRotating] = useState(false)

  const createLounge = useMutation(api.lounges.mutations.createLounge)
  const updateAvatar = useMutation(api.lounges.mutations.updateLoungeAvatar)
  const updateTableColor = useMutation(api.lounges.mutations.updateLoungeTableColor)
  const rotateCode = useMutation(api.lounges.mutations.rotateLoungeJoinCode)

  const { showConfirm, dialogPortal } = useConfirmDialog()

  const handleBack = useCallback(() => {
    if (router.canGoBack()) router.back()
    else router.replace('/(root)/(tabs)/play' as any)
  }, [router])

  const handleEnterLounge = useCallback(() => {
    const loungeId = createdLounge?.loungeId
    if (!loungeId) return
    router.replace({ pathname: '/lounges/[loungeId]', params: { loungeId } } as any)
  }, [createdLounge?.loungeId, router])

  const handleRotateCode = useCallback(() => {
    const loungeId = createdLounge?.loungeId
    if (!loungeId) return
    showConfirm({
      title: translate('loungeSettingsScreen.rotateAlert.title'),
      message: translate('loungeSettingsScreen.rotateAlert.message'),
      cancelText: translate('loungeSettingsScreen.rotateAlert.cancel'),
      confirmText: translate('loungeSettingsScreen.rotateAlert.confirm'),
      onConfirm: async () => {
        setRotating(true)
        try {
          const result = await rotateCode({ loungeId, actorId: userId })
          setCreatedLounge((prev) => (prev ? { ...prev, joinCode: result.joinCode } : prev))
        } finally {
          setRotating(false)
        }
      }
    })
  }, [createdLounge?.loungeId, userId, rotateCode, showConfirm])

  const handleCodeSaved = useCallback((joinCode: string) => {
    setCreatedLounge((prev) => (prev ? { ...prev, joinCode } : prev))
  }, [])
  const handleSaveCode = useSaveLoungeJoinCode(createdLounge?.loungeId, userId, handleCodeSaved)

  if (!user?.auth0Id || user.auth0Id === '') return null

  const handleCreate = async () => {
    if (!name.trim()) return
    setCreating(true)
    setError(null)
    try {
      const result = await createLounge({
        name: name.trim(),
        ownerId: userId,
        userName,
        mongoUserId
      })
      const loungeId = result.loungeId

      if (selectedAvatar) {
        await updateAvatar({ loungeId, actorId: userId, avatar: selectedAvatar })
      }
      if (selectedColor !== null) {
        await updateTableColor({ loungeId, actorId: userId, primaryColor: selectedColor })
      }

      setEvent(events.lounge.create_lounge_submitted, {
        userId,
        loungeId,
        loungeName: name.trim(),
        isTeacher,
        isAdmin
      })
      setCreatedLounge({ loungeId, joinCode: result.joinCode, name: name.trim() })
    } catch (err: any) {
      setError(err.message ?? translate('createLoungeScreen.createError'))
    } finally {
      setCreating(false)
    }
  }

  if (createdLounge) {
    return (
      <SafeAreaView style={styles.container}>
        {dialogPortal}
        <StatusBar style="dark" />

        <View style={styles.header}>
          <Pressable
            onPress={handleBack}
            style={styles.headerButton}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
          </Pressable>
        </View>

        <View style={styles.shareContent}>
          <LoungeCodeCard
            mode="share"
            joinCode={createdLounge.joinCode}
            onRotateCode={handleRotateCode}
            canRotate
            rotating={rotating}
            loungeId={createdLounge.loungeId}
            onSaveCode={handleSaveCode}
          />

          <DS.Button type="On Light" variant="Large/Primary" backgroundColor="Brand/Maroon" onPress={handleEnterLounge}>
            {translate('loungeShareCard.enterLounge')}
          </DS.Button>
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container}>
      {dialogPortal}
      <StatusBar style="dark" />

      <View style={styles.header}>
        <Pressable
          onPress={handleBack}
          style={styles.headerButton}
          accessibilityRole="button"
          accessibilityLabel="Back"
        >
          <DS.Icon name="Arrow---left" color="Global/Black" size={24} />
        </Pressable>
        <DS.Text variant="Heading 3/Medium">{translate('lounges.createALounge')}</DS.Text>
        <View style={styles.headerButton} />
      </View>

      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.formCard}>
          {/* Lounge Name */}
          <View style={styles.fieldGroup}>
            <DS.Text variant="Mini/Bold" style={styles.fieldLabel} uppercase>
              {translate('createLoungeScreen.loungeNameLabel')}
            </DS.Text>
            <TextInput
              style={styles.input}
              placeholder={translate('createLoungeScreen.loungeNamePlaceholder')}
              placeholderTextColor={DS.palette['Brand/Dark Grey']}
              value={name}
              onChangeText={setName}
              autoFocus
              returnKeyType="next"
            />
          </View>

          {/* Short Description */}
          <View style={styles.fieldGroup}>
            <DS.Text variant="Mini/Bold" style={styles.fieldLabel} uppercase>
              {translate('createLoungeScreen.shortDescriptionLabel')}
            </DS.Text>
            <TextInput
              style={styles.input}
              placeholder={translate('createLoungeScreen.shortDescriptionPlaceholder')}
              placeholderTextColor={DS.palette['Brand/Dark Grey']}
              value={description}
              onChangeText={setDescription}
              returnKeyType="done"
              onSubmitEditing={handleCreate}
            />
          </View>

          {/* Lounge Avatar */}
          <View style={styles.fieldGroup}>
            <DS.Text variant="Mini/Bold" style={styles.fieldLabel} uppercase>
              {translate('createLoungeScreen.loungeAvatarLabel')}
            </DS.Text>
            <View style={styles.pickerCard}>
              <LoungeAvatarPicker loungeName={name} currentAvatar={selectedAvatar} onSelect={setSelectedAvatar} />
            </View>
          </View>

          {/* Table Color */}
          <View style={styles.fieldGroup}>
            <DS.Text variant="Mini/Bold" style={styles.fieldLabel} uppercase>
              {translate('createLoungeScreen.tableColorLabel')}
            </DS.Text>
            <View style={styles.pickerCard}>
              <LoungeTableColorPicker currentColor={selectedColor} onSelect={setSelectedColor} />
            </View>
          </View>

          {error && (
            <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Utility/Error'] }}>
              {error}
            </DS.Text>
          )}
        </View>

        <DS.Button
          type="On Light"
          variant="Large/Primary"
          backgroundColor="Brand/Maroon"
          onPress={handleCreate}
          disabled={creating || !name.trim()}
        >
          {creating ? translate('createLoungeScreen.creating') : translate('createLoungeScreen.createButton')}
        </DS.Button>
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    height: 54
  },
  headerButton: {
    padding: 4,
    width: 40,
    alignItems: 'center'
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 32,
    gap: 32
  },
  shareContent: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 32,
    gap: 32
  },
  formCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 24,
    gap: 16,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  fieldGroup: {
    gap: 8
  },
  fieldLabel: {
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 0.5
  },
  input: {
    height: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 12,
    backgroundColor: DS.palette['Global/White'],
    fontSize: 16,
    color: DS.palette['Global/Black']
  },
  pickerCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  }
})
